import React, { useState, useEffect } from 'react';
import { FaLanguage, FaTimes, FaSpinner, FaVolumeUp, FaBook, FaGlobe, FaGripVertical } from 'react-icons/fa';
import { translateLyrics } from '../lib/openai';
import { elevenLabsListener } from '../lib/listener';
import toast from 'react-hot-toast';

interface SelectedParagraphProps {
  selectedText: string;
  onClose: () => void;
  position?: { x: number; y: number };
}

type TargetLanguage = 'english' | 'french' | 'haitian creole';

interface Translation {
  language: TargetLanguage;
  text: string;
  explanation?: string;
}

export default function SelectedParagraph({ selectedText, onClose, position }: SelectedParagraphProps) {
  const [translations, setTranslations] = useState<Translation[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<TargetLanguage>('spanish');
  const [showExplanation, setShowExplanation] = useState(false);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [componentPosition, setComponentPosition] = useState(position || { x: 50, y: 50 });

  const languages: { value: TargetLanguage; label: string; flag: string }[] = [
    { value: 'english', label: 'English', flag: '🇺🇸' },
    { value: 'french', label: 'French', flag: '🇫🇷' },
    { value: 'haitian creole', label: 'Haitian Creole', flag: '🇭🇹' }
  ];

  useEffect(() => {
    // Auto-translate to English when component mounts
    handleTranslate('english');
  }, [selectedText]);

  // Handle dragging functionality
  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.drag-handle')) {
      setIsDragging(true);
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const newX = Math.max(0, Math.min(window.innerWidth - 320, e.clientX - dragOffset.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 400, e.clientY - dragOffset.y));
      setComponentPosition({ x: newX, y: newY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  // Touch events for mobile dragging
  const handleTouchStart = (e: React.TouchEvent) => {
    if ((e.target as HTMLElement).closest('.drag-handle')) {
      const touch = e.touches[0];
      setIsDragging(true);
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setDragOffset({
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      });
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (isDragging && e.touches[0]) {
      e.preventDefault();
      const touch = e.touches[0];
      const newX = Math.max(0, Math.min(window.innerWidth - 320, touch.clientX - dragOffset.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 400, touch.clientY - dragOffset.y));
      setComponentPosition({ x: newX, y: newY });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      return () => {
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, dragOffset]);

  const handleTranslate = async (targetLanguage: TargetLanguage) => {
    if (!selectedText.trim()) return;

    setLoading(true);
    try {
      // Check if we already have this translation
      const existingTranslation = translations.find(t => t.language === targetLanguage);
      if (existingTranslation) {
        setSelectedLanguage(targetLanguage);
        setLoading(false);
        return;
      }

      // Generate translation with explanation
      const translationPrompt = `Translate this English text to ${targetLanguage} and provide a brief explanation of the meaning:

"${selectedText}"

Please format your response as:
Translation: [translated text]
Explanation: [brief explanation of the meaning and context]`;

      const response = await translateLyrics(translationPrompt, targetLanguage);
      
      // Parse the response to extract translation and explanation
      const lines = response.split('\n');
      let translation = '';
      let explanation = '';
      
      for (const line of lines) {
        if (line.toLowerCase().startsWith('translation:')) {
          translation = line.substring(line.indexOf(':') + 1).trim();
        } else if (line.toLowerCase().startsWith('explanation:')) {
          explanation = line.substring(line.indexOf(':') + 1).trim();
        }
      }

      // If parsing failed, use the whole response as translation
      if (!translation) {
        translation = response;
      }

      const newTranslation: Translation = {
        language: targetLanguage,
        text: translation,
        explanation: explanation || undefined
      };

      setTranslations(prev => [...prev.filter(t => t.language !== targetLanguage), newTranslation]);
      setSelectedLanguage(targetLanguage);
    } catch (error: any) {
      console.error('Translation error:', error);
      toast.error(`Failed to translate to ${targetLanguage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAudio = async () => {
    const currentTranslation = translations.find(t => t.language === selectedLanguage);
    if (!currentTranslation || !elevenLabsListener.isAvailable()) return;

    setGeneratingAudio(true);
    try {
      let audioUrl: string | null = null;

      if (selectedLanguage === 'english') {
        audioUrl = await elevenLabsListener.generateSpanishSpeech(
          currentTranslation.text,
          'maria', // Use Maria voice for Spanish
          0.8,     // Slightly slower for learning
          0.9,     // High stability
          0.9      // High clarity
        );
      } else {
        // For French and Haitian Creole, audio generation is not available
        // For now, show a message that audio is only available for English
        toast.info('Audio generation is currently only available for English translations');
        return;
      }

      if (audioUrl) {
        setAudioUrl(audioUrl);
        // Play the audio
        const audio = new Audio(audioUrl);
        audio.play().catch(error => {
          console.error('Error playing audio:', error);
          toast.error('Failed to play audio');
        });
      }
    } catch (error: any) {
      console.error('Audio generation error:', error);
      toast.error('Failed to generate audio');
    } finally {
      setGeneratingAudio(false);
    }
  };

  const currentTranslation = translations.find(t => t.language === selectedLanguage);

  return (
    <div 
      className={`fixed z-50 bg-gradient-to-br from-gray-900/95 via-black/95 to-gray-800/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-600/50 w-80 sm:w-96 max-w-[90vw] max-h-[80vh] overflow-hidden ${
        isDragging ? 'cursor-grabbing select-none' : 'cursor-default'
      }`}
      style={{
        left: `${componentPosition.x}px`,
        top: `${componentPosition.y}px`,
        transform: 'none'
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-600/50 bg-gradient-to-r from-gray-800/50 to-gray-700/50">
        <div className="flex items-center space-x-2 flex-1">
          <div className="drag-handle cursor-grab active:cursor-grabbing p-1 rounded hover:bg-white/10 transition-colors">
            <FaGripVertical className="w-4 h-4 text-gray-400" />
          </div>
          <FaBook className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
          <h3 className="text-base sm:text-lg font-semibold text-white truncate">Text Analysis</h3>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-full hover:bg-white/10 transition-colors duration-200 flex-shrink-0"
        >
          <FaTimes className="w-4 h-4 text-gray-400 hover:text-white" />
        </button>
      </div>

      {/* Scrollable Content Container */}
      <div className="flex flex-col h-full max-h-[70vh] overflow-hidden">
        {/* Selected Text */}
        <div className="p-3 sm:p-4 border-b border-gray-600/50 flex-shrink-0">
          <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-600/30">
            <p className="text-xs sm:text-sm text-gray-400 mb-2">Selected Text:</p>
            <div className="max-h-20 sm:max-h-24 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent">
              <p className="text-sm sm:text-base text-white font-medium leading-relaxed">{selectedText}</p>
            </div>
          </div>
        </div>

        {/* Language Selection */}
        <div className="p-2 border-b border-gray-600/50 flex-shrink-0">
          <p className="text-xs text-gray-400 mb-2">Translate to:</p>
          <div className="grid grid-cols-3 gap-2">
            {languages.map((lang) => (
              <button
                key={lang.value}
                onClick={() => {
                  setSelectedLanguage(lang.value);
                  if (!translations.find(t => t.language === lang.value)) {
                    handleTranslate(lang.value);
                  }
                }}
                disabled={loading}
                className={`flex flex-col items-center p-1.5 rounded-lg border-2 transition-all duration-200 ${
                  selectedLanguage === lang.value
                    ? 'border-yellow-400 bg-yellow-400/20'
                    : 'border-gray-600 hover:border-yellow-400/50 hover:bg-gray-700/50'
                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span className="text-base mb-0.5">{lang.flag}</span>
                <span className="text-xs font-medium text-white">{lang.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Translation Content - Scrollable */}
        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent">
          <div className="p-3 sm:p-4">
            {loading ? (
              <div className="flex items-center justify-center py-6 sm:py-8">
                <FaSpinner className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400 animate-spin mr-3" />
                <span className="text-gray-300 text-sm sm:text-base">Translating...</span>
              </div>
            ) : currentTranslation ? (
              <div className="space-y-4">
                {/* Translation */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs sm:text-sm font-medium text-gray-300">
                      {languages.find(l => l.value === selectedLanguage)?.flag} Translation:
                    </p>
                    {selectedLanguage === 'spanish' && elevenLabsListener.isAvailable() && (
                      <button
                        onClick={handleGenerateAudio}
                        disabled={generatingAudio}
                        className="flex items-center space-x-1 px-2 py-1 bg-green-500/20 hover:bg-green-500/30 rounded-lg transition-colors duration-200 disabled:opacity-50 border border-green-500/30"
                      >
                        {generatingAudio ? (
                          <FaSpinner className="w-3 h-3 animate-spin text-green-400" />
                        ) : (
                          <FaVolumeUp className="w-3 h-3 text-green-400" />
                        )}
                        <span className="text-xs text-green-400">
                          {generatingAudio ? 'Generating...' : 'Listen'}
                        </span>
                      </button>
                    )}
                  </div>
                  <div className="bg-gradient-to-r from-yellow-400/20 to-yellow-500/20 rounded-lg p-3 border border-yellow-400/30">
                    <p className="text-white font-medium text-sm sm:text-base leading-relaxed">{currentTranslation.text}</p>
                  </div>
                </div>

                {/* Explanation */}
                {currentTranslation.explanation && (
                  <div>
                    <button
                      onClick={() => setShowExplanation(!showExplanation)}
                      className="flex items-center space-x-2 text-xs sm:text-sm text-gray-400 hover:text-white mb-2 transition-colors"
                    >
                      <FaGlobe className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span>{showExplanation ? 'Hide' : 'Show'} Explanation</span>
                    </button>
                    
                    {showExplanation && (
                      <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg p-3 border border-blue-500/30">
                        <p className="text-xs sm:text-sm text-blue-200 leading-relaxed">{currentTranslation.explanation}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 pt-2">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(currentTranslation.text);
                      toast.success('Translation copied to clipboard!');
                    }}
                    className="flex-1 px-3 py-2 bg-gray-700/50 hover:bg-gray-600/50 rounded-lg text-xs sm:text-sm font-medium text-white transition-colors duration-200 border border-gray-600/30"
                  >
                    Copy Translation
                  </button>
                  <button
                    onClick={() => handleTranslate(selectedLanguage)}
                    disabled={loading}
                    className="flex-1 px-3 py-2 bg-yellow-500/80 hover:bg-yellow-500 rounded-lg text-xs sm:text-sm font-medium text-black transition-colors duration-200 disabled:opacity-50"
                  >
                    Retranslate
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 sm:py-8">
                <FaLanguage className="w-10 h-10 sm:w-12 sm:h-12 text-gray-500 mx-auto mb-3" />
                <p className="text-gray-400 text-sm sm:text-base">Select a language to translate</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 sm:p-4 border-t border-gray-600/50 flex-shrink-0">
          <div className="bg-gradient-to-r from-gray-800/50 to-gray-700/50 rounded-lg p-3 border border-gray-600/30">
            <p className="text-xs text-gray-400 text-center leading-relaxed">
              💡 Select any text in the chat to get instant translations and explanations
            </p>
          </div>
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        .scrollbar-thin {
          scrollbar-width: thin;
        }
        
        .scrollbar-thumb-gray-600 {
          scrollbar-color: rgba(75, 85, 99, 0.8) transparent;
        }
        
        .scrollbar-track-transparent {
          scrollbar-color: rgba(75, 85, 99, 0.8) transparent;
        }
        
        /* Webkit scrollbar styles */
        .scrollbar-thin::-webkit-scrollbar {
          width: 4px;
        }
        
        .scrollbar-thin::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background-color: rgba(75, 85, 99, 0.8);
          border-radius: 2px;
        }
        
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background-color: rgba(107, 114, 128, 1);
        }
      `}</style>
    </div>
  );
}